import React, { useState, useEffect } from 'react';
import { Menu, X, LayoutGrid, Route, Smartphone, Tag, LogIn, ChevronRight, Download } from 'lucide-react';
import Logo from '../assets/logo.png';
import LandingButton from './LandingButton';
import SpecularButton from './SpecularButton';
import LandingContainer from './LandingContainer';
import LanguageSwitcher from './LanguageSwitcher';
import { scrollToSection } from './scrollUtils';
import { SIGNUP_URL, LOGIN_URL } from './urls';
import { Link } from 'react-router-dom';
import { useT, useLang, downloadPathFor } from './i18n';

const LandingNav = () => {
  const t = useT();
  const downloadPath = downloadPathFor(useLang());
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (id) => {
    scrollToSection(id);
    setMobileMenuOpen(false);
  };

  return (
    <>
      {mobileMenuOpen && (
        <button
          type="button"
          className="landing-nav__scrim"
          aria-label={t('nav.closeMenu')}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    <nav className={`landing-nav ${scrolled ? 'landing-nav--scrolled' : 'landing-nav--transparent'}${mobileMenuOpen ? ' landing-nav--menu-open' : ''}`}>
      <LandingContainer className="landing-nav__inner">
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="landing-nav__brand"
        >
          <img src={Logo} alt="Effzone" className="landing-nav__logo" />
          <span className="landing-nav__title">Effzone</span>
        </button>

        <div className="landing-nav__links">
          <button type="button" onClick={() => handleNavClick('funkcie')} className="landing-nav__link">
            {t('nav.features')}
          </button>
          <button type="button" onClick={() => handleNavClick('ako-to-funguje')} className="landing-nav__link">
            {t('nav.howItWorks')}
          </button>
          <button type="button" onClick={() => handleNavClick('pre-zamestnancov')} className="landing-nav__link">
            {t('nav.forEmployees')}
          </button>
          <button type="button" onClick={() => handleNavClick('cennik')} className="landing-nav__link">
            {t('nav.pricing')}
          </button>
          <Link to={downloadPath} className="landing-nav__link">
            {t('nav.download')}
          </Link>
          <a href={LOGIN_URL} className="landing-nav__link landing-nav__link--bold">
            {t('nav.login')}
          </a>
          <LanguageSwitcher variant="popover" />
          <SpecularButton
            href={SIGNUP_URL}
            size="sm"
            className="specular-button--brand"
            radius={8}
            lineColor="#ffffff"
            baseColor="#1d4ed8"
            intensity={1.1}
            shineSize={12}
            shineFade={44}
            thickness={1.1}
            proximity={260}
            followMouse
          >
            {t('nav.tryFree')}
          </SpecularButton>
        </div>

        {/* Kompaktné CTA na mobile — medzi hero a cenníkom inak žiadne nie je */}
        <div className="landing-nav__mobile-actions">
          <SpecularButton
            href={SIGNUP_URL}
            size="sm"
            className="specular-button--brand landing-nav__cta-m"
            radius={8}
            lineColor="#ffffff"
            baseColor="#1d4ed8"
            intensity={1.1}
            shineSize={12}
            shineFade={44}
            thickness={1.1}
            proximity={260}
            followMouse
          >
            {t('nav.tryFree')}
          </SpecularButton>
          <button
            type="button"
            className="landing-nav__toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            aria-expanded={mobileMenuOpen}
            aria-controls="landing-mobile-menu"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </LandingContainer>

      {mobileMenuOpen && (
        <div className="landing-nav__mobile" id="landing-mobile-menu">
          <button type="button" onClick={() => handleNavClick('funkcie')} className="landing-nav__mobile-link">
            <span className="landing-nav__mobile-icon"><LayoutGrid size={20} strokeWidth={2} /></span>
            <span className="landing-nav__mobile-label">{t('nav.features')}</span>
            <ChevronRight size={18} className="landing-nav__mobile-chev" aria-hidden />
          </button>
          <button type="button" onClick={() => handleNavClick('ako-to-funguje')} className="landing-nav__mobile-link">
            <span className="landing-nav__mobile-icon"><Route size={20} strokeWidth={2} /></span>
            <span className="landing-nav__mobile-label">{t('nav.howItWorks')}</span>
            <ChevronRight size={18} className="landing-nav__mobile-chev" aria-hidden />
          </button>
          <button type="button" onClick={() => handleNavClick('pre-zamestnancov')} className="landing-nav__mobile-link">
            <span className="landing-nav__mobile-icon"><Smartphone size={20} strokeWidth={2} /></span>
            <span className="landing-nav__mobile-label">{t('nav.forEmployees')}</span>
            <ChevronRight size={18} className="landing-nav__mobile-chev" aria-hidden />
          </button>
          <button type="button" onClick={() => handleNavClick('cennik')} className="landing-nav__mobile-link">
            <span className="landing-nav__mobile-icon"><Tag size={20} strokeWidth={2} /></span>
            <span className="landing-nav__mobile-label">{t('nav.pricing')}</span>
            <ChevronRight size={18} className="landing-nav__mobile-chev" aria-hidden />
          </button>
          <Link
            to={downloadPath}
            className="landing-nav__mobile-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className="landing-nav__mobile-icon"><Download size={20} strokeWidth={2} /></span>
            <span className="landing-nav__mobile-label">{t('nav.download')}</span>
            <ChevronRight size={18} className="landing-nav__mobile-chev" aria-hidden />
          </Link>
          <a href={LOGIN_URL} className="landing-nav__mobile-link landing-nav__mobile-link--divider">
            <span className="landing-nav__mobile-icon"><LogIn size={20} strokeWidth={2} /></span>
            <span className="landing-nav__mobile-label">{t('nav.login')}</span>
            <ChevronRight size={18} className="landing-nav__mobile-chev" aria-hidden />
          </a>
          <div className="landing-nav__mobile-lang">
            <LanguageSwitcher />
          </div>
          <LandingButton variant="primary" fullWidth href={SIGNUP_URL} className="landing-nav__mobile-cta">
            {t('nav.tryFree3')}
          </LandingButton>
        </div>
      )}
    </nav>
    </>
  );
};

export default LandingNav;
